/**
 * ProvisionLabModal — provision a NEW laboratory + its first owner, atomically. Two sections in one
 * dialog: the lab governance block (Pharmakon-issued code, raison sociale, deploy class, optional
 * location/contact) and the first-owner block (the bootstrap Proprietor). Delegates to
 * POST /management/labs (management.lab.onboard); the server is the trust boundary — it bcrypts the owner
 * password under the iam policy, mints the est_proprietaire membership, and rejects a reference collision /
 * duplicate owner email with a typed 409. Client-side validation is a convenience only.
 *
 * On success the management root is invalidated (the directory + the Overview KPIs re-read) and a toast
 * confirms; the dialog is non-dismissable while the write is in flight.
 */
import { useState } from "react";
import { Button, FormField, Input, Modal, Select, useToast } from "@/ui";
import { ApiError } from "@/api";
import { useT } from "@/app";
import { useProvisionLab } from "../screens/onboarding.api";
import type { DeployClass, TenancyTier } from "../screens/onboarding.types";

export interface ProvisionLabModalProps {
  open: boolean;
  onClose: () => void;
}

const DEPLOY_CLASSES: ReadonlyArray<DeployClass> = ["local", "cloud", "hybrid"];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ProvisionLabModal({ open, onClose }: ProvisionLabModalProps) {
  const { t } = useT();
  const toast = useToast();
  const provision = useProvisionLab();

  // lab block
  const [reference, setReference] = useState("");
  const [raisonSociale, setRaisonSociale] = useState("");
  const [nomCommercial, setNomCommercial] = useState("");
  const [deployClass, setDeployClass] = useState<DeployClass>("cloud");
  const [tenancyTier, setTenancyTier] = useState<TenancyTier>("partage");
  const [wilaya, setWilaya] = useState("");
  const [commune, setCommune] = useState("");
  const [telephone, setTelephone] = useState("");
  const [labEmail, setLabEmail] = useState("");
  // owner block
  const [prenom, setPrenom] = useState("");
  const [nom, setNom] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [password, setPassword] = useState("");

  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setReference("");
    setRaisonSociale("");
    setNomCommercial("");
    setDeployClass("cloud");
    setTenancyTier("partage");
    setWilaya("");
    setCommune("");
    setTelephone("");
    setLabEmail("");
    setPrenom("");
    setNom("");
    setOwnerEmail("");
    setPassword("");
    setError(null);
  };

  const close = () => {
    if (provision.isPending) return;
    reset();
    onClose();
  };

  const submit = () => {
    setError(null);
    if (
      !reference.trim() ||
      !raisonSociale.trim() ||
      !nomCommercial.trim() ||
      !prenom.trim() ||
      !nom.trim() ||
      !ownerEmail.trim() ||
      !password
    ) {
      setError(t("mgmt.onboarding.field.required"));
      return;
    }
    if (!EMAIL_RE.test(ownerEmail.trim()) || (labEmail.trim() && !EMAIL_RE.test(labEmail.trim()))) {
      setError(t("mgmt.onboarding.field.invalidEmail"));
      return;
    }
    provision.mutate(
      {
        laboratoire: {
          reference: reference.trim(),
          raison_sociale: raisonSociale.trim(),
          nom_commercial: nomCommercial.trim(),
          deploy_class: deployClass,
          tenancy_tier: tenancyTier,
          ...(wilaya.trim() ? { wilaya: wilaya.trim() } : {}),
          ...(commune.trim() ? { commune: commune.trim() } : {}),
          ...(telephone.trim() ? { telephone: telephone.trim() } : {}),
          ...(labEmail.trim() ? { email: labEmail.trim() } : {}),
        },
        owner: {
          prenom: prenom.trim(),
          nom: nom.trim(),
          email: ownerEmail.trim(),
          password,
        },
      },
      {
        onSuccess: (res) => {
          toast.success(
            t("mgmt.onboarding.provision.done", {
              name: res.laboratoire.raisonSociale ?? res.laboratoire.reference ?? "",
            }),
          );
          reset();
          onClose();
        },
        onError: (err) =>
          setError(err instanceof ApiError ? err.message : t("mgmt.onboarding.error.generic")),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={close}
      dismissable={!provision.isPending}
      title={t("mgmt.onboarding.provision.title")}
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={provision.isPending}>
            {t("mgmt.onboarding.action.cancel")}
          </Button>
          <Button onClick={submit} loading={provision.isPending}>
            {t("mgmt.onboarding.provision.submit")}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
        {/* ── Lab block ───────────────────────────────────────────────── */}
        <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
          <legend
            style={{
              padding: 0,
              fontSize: "var(--nv-fs-label)",
              fontWeight: 600,
              color: "var(--nv-text-2, var(--nv-slate))",
            }}
          >
            {t("mgmt.onboarding.provision.lab")}
          </legend>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--nv-s-3)",
              marginTop: "var(--nv-s-2)",
            }}
          >
            <div style={{ display: "grid", gap: "var(--nv-s-3)", gridTemplateColumns: "1fr 1fr" }}>
              <FormField label={t("mgmt.onboarding.provision.reference")} required>
                {(p) => (
                  <Input
                    {...p}
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    autoComplete="off"
                  />
                )}
              </FormField>
              <FormField label={t("mgmt.onboarding.provision.deployClass")} required>
                {(p) => (
                  <Select
                    {...p}
                    value={deployClass}
                    onChange={(e) => setDeployClass(e.target.value as DeployClass)}
                    options={DEPLOY_CLASSES.map((d) => ({ value: d, label: d }))}
                  />
                )}
              </FormField>
            </div>
            <FormField label={t("mgmt.onboarding.provision.raisonSociale")} required>
              {(p) => (
                <Input {...p} value={raisonSociale} onChange={(e) => setRaisonSociale(e.target.value)} />
              )}
            </FormField>
            <FormField label={t("mgmt.onboarding.provision.nomCommercial")} required>
              {(p) => (
                <Input {...p} value={nomCommercial} onChange={(e) => setNomCommercial(e.target.value)} />
              )}
            </FormField>
            <div style={{ display: "grid", gap: "var(--nv-s-3)", gridTemplateColumns: "1fr 1fr" }}>
              <FormField label={t("mgmt.onboarding.provision.tenancyTier")}>
                {(p) => (
                  <Select
                    {...p}
                    value={tenancyTier}
                    onChange={(e) => setTenancyTier(e.target.value as TenancyTier)}
                    options={[
                      { value: "partage", label: t("mgmt.onboarding.provision.tenancy.partage") },
                      { value: "dedie", label: t("mgmt.onboarding.provision.tenancy.dedie") },
                    ]}
                  />
                )}
              </FormField>
              <FormField label={t("mgmt.onboarding.provision.email")}>
                {(p) => (
                  <Input {...p} type="email" value={labEmail} onChange={(e) => setLabEmail(e.target.value)} />
                )}
              </FormField>
            </div>
            <div style={{ display: "grid", gap: "var(--nv-s-3)", gridTemplateColumns: "1fr 1fr 1fr" }}>
              <FormField label={t("mgmt.onboarding.provision.wilaya")}>
                {(p) => <Input {...p} value={wilaya} onChange={(e) => setWilaya(e.target.value)} />}
              </FormField>
              <FormField label={t("mgmt.onboarding.provision.commune")}>
                {(p) => <Input {...p} value={commune} onChange={(e) => setCommune(e.target.value)} />}
              </FormField>
              <FormField label={t("mgmt.onboarding.provision.telephone")}>
                {(p) => (
                  <Input {...p} value={telephone} onChange={(e) => setTelephone(e.target.value)} inputMode="tel" />
                )}
              </FormField>
            </div>
          </div>
        </fieldset>

        {/* ── Owner block ─────────────────────────────────────────────── */}
        <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
          <legend
            style={{
              padding: 0,
              fontSize: "var(--nv-fs-label)",
              fontWeight: 600,
              color: "var(--nv-text-2, var(--nv-slate))",
            }}
          >
            {t("mgmt.onboarding.provision.owner")}
          </legend>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--nv-s-3)",
              marginTop: "var(--nv-s-2)",
            }}
          >
            <div style={{ display: "grid", gap: "var(--nv-s-3)", gridTemplateColumns: "1fr 1fr" }}>
              <FormField label={t("mgmt.onboarding.provision.owner.prenom")} required>
                {(p) => <Input {...p} value={prenom} onChange={(e) => setPrenom(e.target.value)} />}
              </FormField>
              <FormField label={t("mgmt.onboarding.provision.owner.nom")} required>
                {(p) => <Input {...p} value={nom} onChange={(e) => setNom(e.target.value)} />}
              </FormField>
            </div>
            <FormField label={t("mgmt.onboarding.provision.owner.email")} required>
              {(p) => (
                <Input
                  {...p}
                  type="email"
                  value={ownerEmail}
                  onChange={(e) => setOwnerEmail(e.target.value)}
                  autoComplete="off"
                />
              )}
            </FormField>
            <FormField label={t("mgmt.onboarding.provision.owner.password")} required>
              {(p) => (
                <Input
                  {...p}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
              )}
            </FormField>
          </div>
        </fieldset>

        {error && (
          <p role="alert" style={{ margin: 0, color: "var(--nv-danger, var(--nv-critical))" }}>
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
